import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Landmark } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SCHEMES } from "@/data/schemes";

const SITE = "https://hireds.in";

export default function SchemesIndex() {
  useEffect(() => {
    const oldTitle = document.title;
    document.title = "Government Schemes 2026 — Eligibility, Benefits & How to Apply | hireds.in";
    const md = document.querySelector('meta[name="description"]');
    const oldMd = md ? md.getAttribute("content") : null;
    if (md) md.setAttribute("content", "Complete guide to top Indian government schemes — PM Vishwakarma, PM Surya Ghar, PMAY 2.0, PM Kisan, Ayushman Bharat, Atal Pension and more. Eligibility, benefits and how to apply.");
    const canon = document.querySelector('link[rel="canonical"]');
    const oldCanon = canon ? canon.getAttribute("href") : null;
    if (canon) canon.setAttribute("href", `${SITE}/schemes`);
    return () => {
      document.title = oldTitle;
      if (md && oldMd != null) md.setAttribute("content", oldMd);
      if (canon && oldCanon != null) canon.setAttribute("href", oldCanon);
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="schemes-index">
      <Breadcrumbs items={[
        { name: "Home", url: "/" },
        { name: "Schemes", url: "/schemes" },
      ]} />
      <header className="mb-7">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Central &amp; State Schemes</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Government Schemes 2026 — Full Eligibility &amp; Benefits Guide
        </h1>
        <p className="text-base text-slate-700 mt-2 leading-relaxed">
          Plain-English summaries of {SCHEMES.length} most-searched Indian government schemes. Every entry has eligibility, current benefit amount, how to apply, and the official portal link. Verified against PIB releases &amp; ministry pages.
        </p>
      </header>

      <ul className="grid sm:grid-cols-2 gap-4">
        {SCHEMES.map((s) => (
          <li key={s.slug}>
            <Link
              to={`/schemes/${s.slug}`}
              data-testid={`scheme-card-${s.slug}`}
              className="block h-full p-5 rounded-xl border border-slate-200 bg-white hover:border-orange-400 hover:shadow-md transition"
            >
              <div className="flex items-start gap-3">
                <span className="inline-grid place-items-center w-9 h-9 rounded-md bg-orange-100 text-orange-700 shrink-0">
                  <Landmark className="w-4 h-4" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-lg font-extrabold text-slate-900 leading-snug" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
                    {s.name}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">{s.ministry}</p>
                  <p className="text-sm text-slate-700 mt-2 line-clamp-3">{s.benefit}</p>
                </div>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      <section className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-5 md:p-6">
        <h2 className="text-xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
          Disclaimer
        </h2>
        <p className="text-sm text-slate-700 leading-relaxed">
          hireds.in is not affiliated with any government department. Scheme details are summarised from official sources for educational use. Always confirm latest eligibility and benefit amount on the official portal before applying.
        </p>
      </section>
    </div>
  );
}
